import { useEffect, useState } from 'react';
import {
  clearPendingRotatedLoginCode,
  PERSONAL_AUTH_PROVIDERS,
  readPendingRotatedLoginCode,
} from '../api/personalCodeAuthApi.js';
import { usePersonalCodeAuthForm } from '../hooks/usePersonalCodeAuthForm.js';

function Notice({ notice, mobile }) {
  if (!notice.message) return null;
  return <p className={`${mobile ? 'mobile-auth-notice' : 'auth-notice'} ${notice.type}`} role="status">{notice.message}</p>;
}

function LoginForm({ form, mobile }) {
  const prefix = mobile ? 'mobile-auth' : 'auth';
  return (
    <form className={`${prefix}-form personal-code-login-form`} onSubmit={form.submitLogin}>
      <label className={mobile ? undefined : 'auth-field'}>
        <span>개인 로그인 코드</span>
        <input
          type="text"
          value={form.loginCode}
          onChange={(event) => form.setLoginCode(event.target.value.toUpperCase())}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck="false"
          placeholder="DP-XXXX-XXXX-XXXX"
          disabled={form.submitting}
          required
        />
      </label>
      <Notice notice={form.notice} mobile={mobile} />
      <button className={mobile ? 'mobile-auth-submit' : 'auth-submit-button'} type="submit" disabled={form.submitting}>
        {form.submitting ? '로그인 중…' : '로그인'}
      </button>
      <p className="personal-code-help">이메일과 비밀번호는 사용하지 않습니다. 개인 코드는 가입 완료 화면 또는 내 정보에서 재발급할 수 있습니다.</p>
    </form>
  );
}

function ProviderTabs({ form }) {
  return (
    <div className="signup-provider-tabs" role="tablist" aria-label="본인 인증 서비스 선택">
      <button
        type="button"
        role="tab"
        aria-selected={form.signupProvider === PERSONAL_AUTH_PROVIDERS.DC_GALLOG}
        className={form.signupProvider === PERSONAL_AUTH_PROVIDERS.DC_GALLOG ? 'active' : ''}
        onClick={() => form.changeSignupProvider(PERSONAL_AUTH_PROVIDERS.DC_GALLOG)}
        disabled={form.submitting}
      >DC 갤로그</button>
      <button
        type="button"
        role="tab"
        aria-selected={form.signupProvider === PERSONAL_AUTH_PROVIDERS.ARCA}
        className={form.signupProvider === PERSONAL_AUTH_PROVIDERS.ARCA ? 'active' : ''}
        onClick={() => form.changeSignupProvider(PERSONAL_AUTH_PROVIDERS.ARCA)}
        disabled={form.submitting}
      >아카라이브</button>
    </div>
  );
}

function SignupForm({ form, mobile }) {
  const submitClass = mobile ? 'mobile-auth-submit' : 'auth-submit-button';
  const fieldClass = mobile ? undefined : 'auth-field';
  const provider = form.challenge?.provider || form.signupProvider;
  const isArca = provider === PERSONAL_AUTH_PROVIDERS.ARCA;

  if (form.signupResult) {
    return (
      <div className="personal-code-issued" role="status">
        <span>개인 로그인 코드</span>
        <strong>{form.signupResult.loginCode}</strong>
        <p>이 코드는 지금 한 번만 전체 표시됩니다. 분실하면 본인 인증을 다시 진행하거나 내 정보에서 재발급해야 합니다.</p>
        <div>
          <button type="button" onClick={() => form.copyText(form.signupResult.loginCode)}>코드 복사</button>
          <button className="primary" type="button" disabled={form.submitting} onClick={form.continueSignup}>
            {form.submitting ? '계정 여는 중…' : '저장했고 계속'}
          </button>
        </div>
        <Notice notice={form.notice} mobile={mobile} />
      </div>
    );
  }

  if (form.challenge) {
    const verificationUrl = form.challenge.verificationUrl || form.challenge.profileUrl || form.challenge.guestbookUrl;
    return (
      <div className="gallog-verification-step">
        <div className="gallog-verification-code">
          <span>{isArca ? '상태 메시지에 입력할 인증코드' : '방명록에 입력할 인증코드'}</span>
          <strong>{form.challenge.verificationCode}</strong>
          <button type="button" onClick={() => form.copyText(form.challenge.verificationCode)}>코드 복사</button>
        </div>
        <ol>
          {isArca ? (
            <>
              <li>아래 버튼으로 본인 아카라이브 프로필을 엽니다.</li>
              <li>프로필 상태 메시지에 인증코드를 그대로 입력하고 저장합니다.</li>
              <li>DoroParty로 돌아와 인증 확인을 누릅니다.</li>
            </>
          ) : (
            <>
              <li>DC에 본인 계정으로 로그인한 뒤 아래 버튼으로 갤로그 방명록을 엽니다.</li>
              <li>방명록에 인증코드를 그대로 작성합니다. 비로그인 글은 인증할 수 없습니다.</li>
              <li>DoroParty로 돌아와 인증 확인을 누릅니다. 작성자 갤로그가 입력한 식별코드와 같아야 합니다.</li>
            </>
          )}
        </ol>
        <a className="gallog-open-link" href={verificationUrl} target="_blank" rel="noreferrer">{isArca ? '아카라이브 프로필 열기' : '갤로그 방명록 열기'}</a>
        <Notice notice={form.notice} mobile={mobile} />
        <button className={submitClass} type="button" disabled={form.submitting} onClick={form.verifyIdentity}>
          {form.submitting ? (isArca ? '상태 메시지 확인 중…' : '작성자 확인 중…') : '인증 확인'}
        </button>
        <button className="personal-code-restart" type="button" disabled={form.submitting} onClick={() => form.changeMode('signup')}>다른 인증 정보 입력</button>
      </div>
    );
  }

  const isArcaSignup = form.signupProvider === PERSONAL_AUTH_PROVIDERS.ARCA;
  return (
    <form className={`${mobile ? 'mobile-auth-form' : 'auth-form'} gallog-start-form`} onSubmit={form.startVerification}>
      <ProviderTabs form={form} />
      <label className={fieldClass}>
        <span>{isArcaSignup ? '아카라이브 닉네임 또는 프로필 주소' : 'DC 갤로그 식별코드'}</span>
        <input
          className={`identity-input ${isArcaSignup ? 'arca' : 'dc'}`}
          type="text"
          value={form.identityValue}
          onChange={(event) => form.setIdentityValue(event.target.value)}
          autoComplete="off"
          spellCheck="false"
          placeholder={isArcaSignup ? '닉네임 입력 또는 https://arca.live/u/@닉네임/숫자' : '식별코드 입력'}
          disabled={form.submitting}
          required
        />
      </label>
      <p className="gallog-public-condition">{isArcaSignup ? '일반 고닉은 닉네임 또는 프로필 주소를 입력할 수 있고, 반고닉은 숫자 식별자가 붙은 프로필 주소(예: /u/@닉네임/97064984)를 그대로 입력하세요. 프로필이 공개 상태이고 상태 메시지를 수정할 수 있어야 합니다.' : '갤로그가 공개 상태여야 합니다. 인증코드는 반드시 DC에 로그인한 본인 계정으로 작성해야 하며 작성자의 갤로그 링크를 대조합니다.'}</p>
      <Notice notice={form.notice} mobile={mobile} />
      <button className={submitClass} type="submit" disabled={form.submitting}>
        {form.submitting ? (isArcaSignup ? '프로필 확인 중…' : '갤로그 확인 중…') : '인증코드 발급'}
      </button>
    </form>
  );
}

export function PersonalCodeAuthContent({ mobile = false }) {
  const form = usePersonalCodeAuthForm();
  const [rotatedCode, setRotatedCode] = useState('');
  const isSignup = form.mode === 'signup';
  const tabsClass = mobile ? 'mobile-auth-tabs' : 'auth-mode-tabs';

  useEffect(() => {
    const code = readPendingRotatedLoginCode();
    if (!code) return;
    setRotatedCode(code);
    form.changeMode('login');
    form.setLoginCode(code);
  }, []);

  const dismissRotatedCode = () => {
    clearPendingRotatedLoginCode();
    setRotatedCode('');
  };

  return (
    <>
      <div className={tabsClass} role="tablist" aria-label="로그인 방식">
        <button className={!isSignup ? 'active' : ''} type="button" onClick={() => form.changeMode('login')}>로그인</button>
        <button className={isSignup ? 'active' : ''} type="button" onClick={() => form.changeMode('signup')}>처음 이용</button>
      </div>
      <div className={mobile ? 'mobile-auth-card-heading' : 'auth-card-heading'}>
        <p className="section-eyebrow">{isSignup ? 'PUBLIC PROFILE VERIFICATION' : 'PERSONAL LOGIN CODE'}</p>
        <h2>{isSignup ? '공개 프로필로 본인 인증' : '개인 코드 로그인'}</h2>
        <p>{isSignup ? 'DC 갤로그 작성자 또는 아카라이브 상태 메시지를 확인한 뒤 개인 로그인 코드를 발급합니다.' : '가입할 때 발급받은 개인 로그인 코드를 입력하세요.'}</p>
      </div>
      {!isSignup && rotatedCode ? (
        <div className="personal-code-issued personal-code-rotated" role="status">
          <span>새 개인 로그인 코드</span>
          <strong>{rotatedCode}</strong>
          <p>로그인 코드가 변경되어 현재 기기를 포함한 기존 로그인은 모두 해제됐습니다. 새 코드로 다시 로그인하세요.</p>
          <div>
            <button type="button" onClick={() => form.copyText(rotatedCode)}>코드 복사</button>
            <button type="button" onClick={dismissRotatedCode}>표시 닫기</button>
          </div>
        </div>
      ) : null}
      {isSignup ? <SignupForm form={form} mobile={mobile} /> : <LoginForm form={form} mobile={mobile} />}
    </>
  );
}
